import {useCallback, useEffect, useMemo, useState} from "react"
import axios from "axios"
import moment from "moment"
import {Alert, Badge, Button, Card, Form, InputGroup, ProgressBar, Spinner, Table} from "react-bootstrap"
import DatePicker, {registerLocale} from "react-datepicker"
import {de, enGB} from "date-fns/locale"
import {useTranslation} from "react-i18next"
import "react-datepicker/dist/react-datepicker.css"

import {useAuth} from "../../context/AuthProvider"

registerLocale("de", de)
registerLocale("en-GB", enGB)

type MinerRun = {
    _id: string
    status: "running" | "paused" | "completed" | "failed" | "aborted"
    mode?: "accelerated" | "full"
    rangeStart: string
    rangeEnd: string
    startedAt: string
    finishedAt?: string
    lastRequestAt?: string
    currentRoom: string
    currentWeek?: string
    roomsDiscovered: number
    roomsSelected?: number
    weeksTotal: number
    requestsTotal: number
    requestsCompleted: number
    requestsSucceeded: number
    requestsFailed: number
    bookingsCached: number
    recentErrors: string[]
    priorityWeeks?: string[]
    priorityWeek?: string
    priorityRequestsTotal?: number
    priorityRequestsCompleted?: number
}

type StatusResponse = {
    runs: MinerRun[]
    cacheEntries: number
    requestDelayMs: number
    acceleratedRoomCount: number
    fullScanIntervalDays: number
}

type ExclusionCandidate = {
    sourceRoomIdentifier: string
    normalizedRoomIdentifier: string | null
    catalogueType: "onDemand" | "pcLab" | null
    tucanRoomType: string | null
    tucanDescription: string
    tucanOverviewUrl: string | null
    excluded: boolean
}

type ExclusionsResponse = {
    candidates: ExclusionCandidate[]
    automaticFoyerCount: number
}

export default function TucanMinerStatusComponent() {
    const {t, i18n} = useTranslation("admin")
    const auth = useAuth()
    const isAdmin = auth.hasGate("admin")
    const [data, setData] = useState<StatusResponse | null>(null)
    const [exclusions, setExclusions] = useState<ExclusionsResponse | null>(null)
    const [loading, setLoading] = useState(true)
    const [acting, setActing] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [priorityDate, setPriorityDate] = useState<Date>(new Date())
    const isGerman = i18n.language.toLowerCase().startsWith("de")

    const load = useCallback(async () => {
        if (!isAdmin) return
        try {
            const [statusResponse, exclusionResponse] = await Promise.all([
                axios.get<StatusResponse>(
                    "/api/schedule/tucan-miner/status",
                    auth.axiosAuthHeader()
                ),
                axios.get<ExclusionsResponse>(
                    "/api/schedule/tucan-miner/exclusions",
                    auth.axiosAuthHeader()
                ),
            ])
            setData(statusResponse.data)
            setExclusions(exclusionResponse.data)
            setError(null)
        } catch (error: unknown) {
            setError(axios.isAxiosError(error)
                ? error.response?.data?.msg ?? t("tucanMiner.messages.loadError")
                : t("tucanMiner.messages.loadError"))
        } finally {
            setLoading(false)
        }
    }, [auth, isAdmin, t])

    useEffect(() => {
        void load()
        const interval = window.setInterval(() => void load(), 5_000)
        return () => window.clearInterval(interval)
    }, [load])

    const current = data?.runs.find(run => run.status === "running" || run.status === "paused")
    const progress = current?.requestsTotal
        ? current.requestsCompleted / current.requestsTotal * 100
        : 0
    const estimatedFinish = useMemo(() => {
        if (!current || current.requestsCompleted === 0 || current.status !== "running") return null
        const elapsed = Date.now() - new Date(current.startedAt).getTime()
        const remaining = current.requestsTotal - current.requestsCompleted
        return new Date(Date.now() + elapsed / current.requestsCompleted * remaining)
    }, [current])

    const action = async (
        name: "start" | "pause" | "resume" | "abort",
        mode?: "accelerated" | "full"
    ) => {
        if (name === "abort" && !window.confirm(
            t("tucanMiner.actions.confirmAbort")
        )) return
        setActing(true)
        setError(null)
        try {
            await axios.post(
                `/api/schedule/tucan-miner/${name}`,
                mode ? {mode} : {},
                auth.axiosAuthHeader()
            )
            await load()
        } catch (error: unknown) {
            setError(axios.isAxiosError(error)
                ? error.response?.data?.msg ?? t("tucanMiner.messages.actionError")
                : t("tucanMiner.messages.actionError"))
        } finally {
            setActing(false)
        }
    }

    const prioritize = async () => {
        setActing(true)
        setError(null)
        try {
            await axios.post(
                "/api/schedule/tucan-miner/prioritize",
                {date: moment(priorityDate).format("YYYY-MM-DD")},
                auth.axiosAuthHeader()
            )
            await load()
        } catch (error: unknown) {
            setError(axios.isAxiosError(error)
                ? error.response?.data?.msg ?? t("tucanMiner.messages.priorityError")
                : t("tucanMiner.messages.priorityError"))
        } finally {
            setActing(false)
        }
    }

    const toggleExclusion = (sourceRoomIdentifier: string) => {
        setExclusions(current => current ? {
            ...current,
            candidates: current.candidates.map(candidate =>
                candidate.sourceRoomIdentifier === sourceRoomIdentifier
                    ? {...candidate, excluded: !candidate.excluded}
                    : candidate
            ),
        } : current)
    }

    const saveExclusions = async () => {
        if (!exclusions) return
        setActing(true)
        setError(null)
        try {
            const response = await axios.put<ExclusionsResponse>(
                "/api/schedule/tucan-miner/exclusions",
                {
                    sourceRoomIdentifiers: exclusions.candidates
                        .filter(candidate => candidate.excluded)
                        .map(candidate => candidate.sourceRoomIdentifier),
                },
                auth.axiosAuthHeader()
            )
            setExclusions(response.data)
        } catch (error: unknown) {
            setError(axios.isAxiosError(error)
                ? error.response?.data?.msg ??
                    t("tucanMiner.messages.exclusionsError")
                : t("tucanMiner.messages.exclusionsError"))
        } finally {
            setActing(false)
        }
    }

    if (!isAdmin) return <div className="component">
        <Alert variant="danger">{t("adminOnly")}</Alert>
    </div>

    return <div className="component">
        <Card className="mb-3">
            <Card.Header><h3 className="mb-0">
                <i className="bi bi-cloud-download me-2"/>{t("tucanMiner.title")}
            </h3></Card.Header>
            <Card.Body>
                {error && <Alert variant="danger">{error}</Alert>}
                {loading ? <Spinner size="sm"/> : <>
                    <div className="d-flex flex-wrap gap-2 mb-3">
                        {!current && <>
                            <Button disabled={acting}
                                onClick={() => action("start", "accelerated")}>
                                <i className="bi bi-lightning-charge me-1"/>
                                {t("tucanMiner.actions.startAccelerated", {
                                    count: data?.acceleratedRoomCount ?? 20,
                                })}
                            </Button>
                            <Button variant="outline-primary" disabled={acting}
                                onClick={() => action("start", "full")}>
                                <i className="bi bi-calendar4-week me-1"/>
                                {t("tucanMiner.actions.startFull")}
                            </Button>
                        </>}
                        {current?.status === "running" && <Button variant="warning"
                            disabled={acting} onClick={() => action("pause")}>
                            {t("tucanMiner.actions.pause")}</Button>}
                        {current?.status === "paused" && <Button variant="success"
                            disabled={acting} onClick={() => action("resume")}>
                            {t("tucanMiner.actions.resume")}</Button>}
                        {current && <Button variant="danger" disabled={acting}
                            onClick={() => action("abort")}>
                            <i className="bi bi-stop-circle me-1"/>
                            {t("tucanMiner.actions.abort")}
                        </Button>}
                        <Badge bg="secondary" className="align-self-center">
                            {t("tucanMiner.cachedWeeks", {count: data?.cacheEntries ?? 0})}
                        </Badge>
                        <Badge bg="info" className="align-self-center">
                            {t("tucanMiner.requestDelay", {
                                seconds: (data?.requestDelayMs ?? 0) / 1000,
                            })}
                        </Badge>
                    </div>
                    <div className="mb-3">
                        <Form.Label>{t("tucanMiner.priority.label")}</Form.Label>
                        <InputGroup>
                            <DatePicker selected={priorityDate}
                                onChange={date => date && setPriorityDate(date)}
                                minDate={new Date()}
                                maxDate={moment().add(18, "months").toDate()}
                                locale={isGerman ? "de" : "en-GB"}
                                dateFormat={isGerman ? "dd.MM.yyyy" : "dd/MM/yyyy"}
                                customInput={<Form.Control/>}
                                wrapperClassName="flex-grow-1"/>
                            <Button variant="outline-primary" disabled={acting}
                                onClick={prioritize}>
                                <i className="bi bi-lightning-charge me-1"/>
                                {t(current
                                    ? "tucanMiner.actions.prioritize"
                                    : "tucanMiner.actions.scanWeek")}
                            </Button>
                        </InputGroup>
                        <Form.Text>
                            {t("tucanMiner.priority.hint")}
                        </Form.Text>
                    </div>
                    {current ? <>
                        <div className="mb-2">
                            <Badge bg={current.mode === "accelerated" ? "info" : "primary"}>
                                {t(`tucanMiner.mode.${current.mode ?? "full"}`)}
                            </Badge>
                            {" "}
                            <span className="text-muted">
                                {t("tucanMiner.roomsSelected", {
                                    selected: current.roomsSelected ?? current.roomsDiscovered,
                                    discovered: current.roomsDiscovered,
                                })}
                            </span>
                        </div>
                        <div className="d-flex justify-content-between">
                            <strong>{current.currentRoom || t("tucanMiner.initializing")}</strong>
                            <span>{progress.toFixed(2)} %</span>
                        </div>
                        <ProgressBar now={progress} variant={current.status === "paused" ? "warning" : "primary"}
                            className="my-2"/>
                        <div className="text-muted">
                            {t("tucanMiner.progressSummary", {
                                completed: current.requestsCompleted,
                                total: current.requestsTotal,
                                succeeded: current.requestsSucceeded,
                                failed: current.requestsFailed,
                                bookings: current.bookingsCached,
                            })}
                        </div>
                        {(current.priorityWeek || (current.priorityWeeks?.length ?? 0) > 0) &&
                            <Alert variant="primary" className="mt-3">
                                <strong>{t("tucanMiner.priority.mode")}</strong>{" "}
                                {current.priorityWeek
                                    ? t("tucanMiner.priority.scanning", {
                                        date: moment(current.priorityWeek).format("L"),
                                    })
                                    : t("tucanMiner.priority.queued", {
                                        count: current.priorityWeeks?.length ?? 0,
                                    })}
                                {" "}· {t("tucanMiner.priority.requests", {
                                    completed: current.priorityRequestsCompleted ?? 0,
                                    total: current.priorityRequestsTotal ?? 0,
                                })}
                            </Alert>}
                        <div className="text-muted">
                            {t("tucanMiner.details", {
                                week: current.currentWeek ? moment(current.currentWeek).format("L") : "–",
                                lastRequest: current.lastRequestAt
                                    ? moment(current.lastRequestAt).format("L LTS") : "–",
                                estimatedFinish: estimatedFinish
                                    ? moment(estimatedFinish).format("L LTS") : "–",
                            })}
                        </div>
                        {current.recentErrors.length > 0 && <Alert variant="warning" className="mt-3 mb-0">
                            <strong>{t("tucanMiner.recentErrors")}</strong>
                            <ul className="mb-0">{current.recentErrors.slice(-10).map((entry, index) =>
                                <li key={`${entry}-${index}`}><small>{entry}</small></li>)}</ul>
                        </Alert>}
                    </> : <Alert variant="info" className="mb-0">
                        {t("tucanMiner.noCurrentRun")}
                    </Alert>}
                </>}
            </Card.Body>
        </Card>
        <Card className="mb-3">
            <Card.Header><h4 className="mb-0">
                {t("tucanMiner.exclusions.title")}
            </h4></Card.Header>
            <Card.Body>
                <Alert variant="info">
                    {t("tucanMiner.exclusions.description", {
                        foyers: exclusions?.automaticFoyerCount ?? 0,
                    })}
                </Alert>
                {!exclusions ? <Spinner size="sm"/> : <>
                    <div className="table-responsive" style={{maxHeight: "28rem"}}>
                        <Table hover size="sm">
                            <thead className="sticky-top bg-body"><tr>
                                <th>{t("tucanMiner.exclusions.exclude")}</th>
                                <th>{t("tucanMiner.exclusions.source")}</th>
                                <th>{t("tucanMiner.exclusions.normalized")}</th>
                                <th>{t("tucanMiner.exclusions.tucanType")}</th>
                                <th>{t("tucanMiner.exclusions.descriptionField")}</th>
                            </tr></thead>
                            <tbody>{exclusions.candidates.map(candidate =>
                                <tr key={candidate.sourceRoomIdentifier}>
                                    <td><Form.Check checked={candidate.excluded}
                                        disabled={acting}
                                        onChange={() => toggleExclusion(
                                            candidate.sourceRoomIdentifier
                                        )}/></td>
                                    <td>{candidate.sourceRoomIdentifier}</td>
                                    <td>{candidate.normalizedRoomIdentifier ?? "–"}</td>
                                    <td>{candidate.tucanRoomType ??
                                        t("tucanMiner.exclusions.unknown")}</td>
                                    <td>{candidate.tucanOverviewUrl
                                        ? <a href={candidate.tucanOverviewUrl}
                                            target="_blank"
                                            rel="noopener noreferrer">
                                            {candidate.tucanDescription || "TUCaN"}
                                        </a>
                                        : candidate.tucanDescription || "–"}</td>
                                </tr>)}</tbody>
                        </Table>
                    </div>
                    <Button onClick={saveExclusions} disabled={acting}>
                        {t("tucanMiner.exclusions.save")}
                    </Button>
                </>}
            </Card.Body>
        </Card>
        <Card>
            <Card.Header><h4 className="mb-0">{t("tucanMiner.runs.title")}</h4></Card.Header>
            <Card.Body>
                <Table responsive hover>
                    <thead><tr><th>{t("tucanMiner.runs.columns.start")}</th>
                        <th>{t("tucanMiner.runs.columns.range")}</th>
                        <th>{t("tucanMiner.runs.columns.status")}</th>
                        <th>{t("tucanMiner.runs.columns.progress")}</th>
                        <th>{t("tucanMiner.runs.columns.errors")}</th>
                        <th>{t("tucanMiner.runs.columns.bookings")}</th></tr></thead>
                    <tbody>{data?.runs.map(run => <tr key={run._id}>
                        <td>{moment(run.startedAt).format("L LTS")}</td>
                        <td>{moment(run.rangeStart).format("L")}–{moment(run.rangeEnd).format("L")}</td>
                        <td><Badge bg={run.status === "completed" ? "success" :
                            run.status === "failed" ? "danger" :
                                run.status === "aborted" ? "secondary" :
                                    run.status === "paused" ? "warning" : "primary"}>
                            {t(`tucanMiner.status.${run.status}`)}
                        </Badge>{" "}
                            <small>{t(`tucanMiner.mode.${run.mode ?? "full"}`)}</small>
                        </td>
                        <td>{run.requestsCompleted}/{run.requestsTotal}</td>
                        <td>{run.requestsFailed}</td>
                        <td>{run.bookingsCached}</td>
                    </tr>)}</tbody>
                </Table>
            </Card.Body>
        </Card>
    </div>
}
